import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MyHead from '../../components/MyHead';
import SelectCountry from '../../components/SelectCountry';
import { Tab } from '@headlessui/react';
import { getLangCountry } from '../../helpers/languages';
import { useRouter } from 'next/router';
import {
  fetchShippingCountriesList,
  getShippingRate
} from '../../fetchers/shipping';
import { formatPrice } from '../../helpers/prices';
import { useAlert } from '../../context/AlertProvider';
import Spinner from '../../components/Spinner';
import { ICountryListItem } from '../../models/footer';

interface ICountryData {
  title: string;
  cost: number;
  asterisk: string;
}

const ShippingPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const alert = useAlert();
  const router = useRouter();
  const { lang, country } = getLangCountry(router.query);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countryData, setCountryData] = useState<ICountryData | null>(null);
  const shippingCountriesList = fetchShippingCountriesList({ lang });
  const [currentShippingCountry, setCurrentShippingCountry] =
    useState<ICountryListItem | null>(null);

  useEffect(() => {
    if (router.query.lang && country && !selectedCountry) {
      setSelectedCountry(country);
    }
  }, [router.query.lang]);

  useEffect(() => {
    if (router.query?.lang && selectedCountry) {
      const actualCountry = localStorage.getItem('uyn_actual_country');

      let country: string;

      if (!actualCountry) {
        country = selectedCountry;
      } else if (
        (selectedCountry === 'eu' || selectedCountry === 'int') &&
        actualCountry
      ) {
        country = actualCountry;
      } else {
        country = selectedCountry;
      }

      const currentShippingCountry = shippingCountriesList.find(c => {
        return c['country-code']?.toLowerCase() === country.toLowerCase();
      })!;

      setCurrentShippingCountry(currentShippingCountry);
    }
  }, [selectedCountry, router.query.lang]);

  useEffect(() => {
    (async () => {
      if (!selectedCountry) {
        return;
      }
      setCountryData(null);
      setLoading(true);

      const { data, error } = await getShippingRate({
        lang,
        country: selectedCountry
      });

      setLoading(false);

      if (error) {
        return alert.error(t('GENERIC_ERROR'));
      }

      setCountryData(data);
    })();
  }, [selectedCountry]);

  const { title = '', cost = 0, asterisk = '' } = countryData || {};

  return (
    <Fragment>
      <MyHead />

      <section className="section space-y-md">
        <h1 className="h2">{t('P_SHIPPING.SHIPPINGS')}</h1>

        <div className="w-full max-w-3xl mx-auto space-y-6">
          <h2 className="h5 font-normal text-center">
            {t('P_SHIPPING.SELECT')}
          </h2>

          <div className="w-full max-w-lg mx-auto">
            <SelectCountry
              country={selectedCountry}
              currentShippingCountry={currentShippingCountry}
              onChange={setSelectedCountry}
            />
          </div>
        </div>

        {loading && <Spinner />}
        {!loading && selectedCountry.toLowerCase() !== 'us' && (
          <Fragment>
            <div className="w-full max-w-xl mx-auto flex flex-col items-center bg-gray-200 space-y-3 py-6">
              <h3 className="h3 tracking-wide">
                {formatPrice(
                  cost,
                  shippingCountriesList,
                  currentShippingCountry
                    ? currentShippingCountry['currency']
                    : ''
                )}{' '}
                *
              </h3>

              <p className="text-xl text-body">{title}</p>
              <p className="text-body">{asterisk && `* ${asterisk}`}</p>
            </div>

            <ul className="max-w-5xl mx-auto space-y-6 divide-y divide-black">
              {selectedCountry.toLowerCase() === 'ch' ? (
                <li className="space-y-6">
                  <p>{t('P_SHIPPING.SWISS_TEXT')}</p>
                </li>
              ):(
                <li className="space-y-6">
                  <h3 className="text-xl">{t('P_SHIPPING.EXTRA_UE_TEXT')}</h3>
                  <p>{t('P_SHIPPING.SHIPPING_TEXT')}</p>
                </li>
              )}

              <li className="space-y-6 pt-12">
                <h3 className="text-xl">
                  {t('P_SHIPPING.SHIPPING_DELAY_TITLE')}
                </h3>
                <p>{t('P_SHIPPING.SHIPPING_DELAY_TEXT')}</p>
              </li>

              <li className="space-y-6 pt-12">
                <h3 className="text-xl">{t('P_SHIPPING.CARRYING_TITLE')}</h3>
                <p>{t('P_SHIPPING.CARRYING_TEXT')}</p>
              </li>
            </ul>
          </Fragment>
        )}

        {!loading && selectedCountry.toLowerCase() == 'us' && (
          <Fragment>
            <div>
              <h3 className="h3 tracking-wide max-w-5xl mx-auto mb-4">
                {t('P_SHIPPING.US.TABLE_TITLE')}
              </h3>
              <table className="max-w-5xl mx-auto w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 lg:pl-8"
                    >
                      {t('P_SHIPPING.US.TABLE_COL1_TITLE')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('P_SHIPPING.US.TABLE_COL2_TITLE')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('P_SHIPPING.US.TABLE_COL3_TITLE')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('P_SHIPPING.US.TABLE_COL4_TITLE')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <tr>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8">
                      {t('P_SHIPPING.US.TABLE_COL1_ROW1')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {t('P_SHIPPING.US.TABLE_COL2_ROW1')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {t('P_SHIPPING.US.TABLE_COL3_ROW1')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {t('P_SHIPPING.US.TABLE_COL4_ROW1')}
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8">
                      {t('P_SHIPPING.US.TABLE_COL1_ROW2')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {t('P_SHIPPING.US.TABLE_COL2_ROW2')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {t('P_SHIPPING.US.TABLE_COL3_ROW2')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {t('P_SHIPPING.US.TABLE_COL4_ROW2')}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div
                className="max-w-5xl mx-auto w-full mt-4"
                dangerouslySetInnerHTML={{
                  __html: t('P_SHIPPING.US.TABLE_ASTERISK')
                }}
              />
            </div>

            <ul className="max-w-5xl mx-auto space-y-6 divide-y divide-black">
              <li className="space-y-6">
                <h3 className="text-xl">{t('P_SHIPPING.US.DELIVERY_TITLE')}</h3>
                <p>{t('P_SHIPPING.US.DELIVERY_TEXT')}</p>
              </li>

              <li className="space-y-6 pt-12">
                <h3 className="text-xl">{t('P_SHIPPING.US.DELAY_TITLE')}</h3>
                <p>{t('P_SHIPPING.US.DELAY_TEXT')}</p>
              </li>

              <li className="space-y-6 pt-12">
                <h3 className="text-xl">
                  {t('P_SHIPPING.US.AFTER_DELIVERY_TITLE')}
                </h3>
                <p>{t('P_SHIPPING.US.AFTER_DELIVERY_TEXT')}</p>
              </li>
            </ul>
          </Fragment>
        )}
      </section>
    </Fragment>
  );
};

export default ShippingPage;
